// src/pages/Servicos.jsx
import React, { useState, useEffect } from 'react';
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  Timestamp,
  query,
  limit,
  startAfter,
  orderBy,
} from 'firebase/firestore';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import ModalNovoServico from '../components/ModalNovoServico';
import { useEmpresaId } from '../context/EmpresaContext';
import {
  PlusCircle, Search, Wrench, Pencil, Trash2, ArrowLeft, ArrowRight
} from 'lucide-react';

const db = getFirestore();

const defaultForm = { nome: '', descricao: '', valorSugerido: '' };

/* ===== Botão tom-sobre-tom (ícone sem fundo) ===== */
const toneStyles = (tone) => {
  switch (tone) {
    case 'primary':
      return 'text-blue-800 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/50 dark:hover:bg-blue-900';
    case 'success':
      return 'text-green-800 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900/50 dark:hover:bg-green-900';
    case 'danger':
      return 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/50 dark:hover:bg-red-900';
    default:
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
  }
};
const ActionBtn = ({ tone='neutral', className='', icon: Icon, disabled, children, ...props }) => {
  const base = `inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md ${toneStyles(tone)} ${
    disabled ? 'opacity-60 cursor-not-allowed' : ''
  } ${className}`;
  return (
    <button className={base} disabled={disabled} {...props}>
      {Icon && <Icon size={16} className="text-current" />} {children}
    </button>
  );
};

const Servicos = () => {
  const empresaId = useEmpresaId();

  const [servicos, setServicos] = useState([]);
  const [busca, setBusca] = useState('');
  const [formVisivel, setFormVisivel] = useState(false);
  const [editandoServico, setEditandoServico] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [ultimoDoc, setUltimoDoc] = useState(null);
  const [temMaisPaginas, setTemMaisPaginas] = useState(false);
  const [erros, setErros] = useState({});
  const [form, setForm] = useState(defaultForm);

  useEffect(() => { if (empresaId) buscarServicos(false, true); }, [empresaId]);

  useEffect(() => {
    if (editandoServico) {
      setForm({
        nome: editandoServico.nome || '',
        descricao: editandoServico.descricao || '',
        valorSugerido:
          editandoServico.valorSugerido !== undefined && editandoServico.valorSugerido !== null
            ? String(editandoServico.valorSugerido)
            : ''
      });
      setFormVisivel(true);
    }
  }, [editandoServico]);

  const validarFormulario = () => {
    const novosErros = {};
    if (!form.nome.trim()) novosErros.nome = 'Nome do serviço é obrigatório';
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const colServicos = (id) => collection(db, 'empresas', id, 'servicos');

  const buscarServicos = async (continuar = false, resetar = false) => {
    if (!empresaId) return;
    let q = query(colServicos(empresaId), orderBy('nome'), limit(12));
    if (continuar && ultimoDoc && !resetar) {
      q = query(colServicos(empresaId), orderBy('nome'), startAfter(ultimoDoc), limit(12));
    }
    const snapshot = await getDocs(q);
    const docs = snapshot.docs;
    setServicos(docs.map(d => ({ id: d.id, ...d.data() })));
    setUltimoDoc(docs[docs.length - 1] || null);
    setTemMaisPaginas(docs.length === 12);
  };

  const handleSalvar = async () => {
    if (!validarFormulario() || !empresaId) return;
    const payload = {
      nome: form.nome,
      descricao: form.descricao,
      valorSugerido: form.valorSugerido ? Number(String(form.valorSugerido).replace(',', '.')) : null,
    };
    try {
      if (editandoServico) {
        await updateDoc(doc(db, 'empresas', empresaId, 'servicos', editandoServico.id), payload);
        await Swal.fire({ icon: 'success', title: 'Serviço atualizado!', confirmButtonText: 'OK' });
      } else {
        await addDoc(colServicos(empresaId), { ...payload, criadoEm: Timestamp.now() });
        await Swal.fire({ icon: 'success', title: 'Serviço cadastrado!', confirmButtonText: 'OK' });
      }
      setFormVisivel(false);
      setEditandoServico(null);
      setForm(defaultForm);
      setPagina(1);
      buscarServicos(false, true);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao salvar serviço',
        text: error.message,
        confirmButtonText: 'OK',
      });
    }
  };

  const handleExcluir = async (id) => {
    if (!empresaId) return;
    const result = await Swal.fire({
      title: 'Deseja excluir este serviço?',
      text: 'Esta ação não poderá ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    });
    if (result.isConfirmed) {
      await deleteDoc(doc(db, 'empresas', empresaId, 'servicos', id));
      setPagina(1);
      buscarServicos(false, true);
      await Swal.fire({ icon: 'success', title: 'Serviço excluído!', confirmButtonText: 'OK' });
    }
  };

  const handleEditar = (servico) => setEditandoServico(servico);
  const handleFecharModal = () => { setFormVisivel(false); setEditandoServico(null); setForm(defaultForm); setErros({}); };

  const servicosFiltrados = servicos.filter((s) => (s.nome || '').toLowerCase().includes(busca.toLowerCase()));

  return (
    <div className="p-6 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Serviços</h1>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-grow">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome..."
              className="border px-3 py-2 pl-10 rounded-lg w-full bg-white dark:bg-gray-800 dark:border-gray-700"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <button
            onClick={() => { setFormVisivel(true); setEditandoServico(null); setForm(defaultForm); setErros({}); }}
            className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
          >
            <PlusCircle size={18} /> Novo Serviço
          </button>
        </div>
      </div>

      {/* Lista/cards */}
      {servicosFiltrados.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl">
          <Wrench size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200">
            Nenhum serviço encontrado
          </h3>
          <p className="text-gray-500 mt-2">Clique em &quot;Novo Serviço&quot; para cadastrar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {servicosFiltrados.map((s) => (
            <div
              key={s.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-md flex flex-col transition hover:shadow-xl hover:-translate-y-1"
            >
              {/* Cabeçalho */}
              <div className="p-4 flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-md">
                    <Wrench className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">{s.nome}</h3>
                  </div>
                </div>
              </div>

              {/* Corpo */}
              <div className="p-4 space-y-3 text-sm flex-grow border-t border-b dark:border-gray-700">
                {s.descricao && (
                  <p className="text-gray-600 dark:text-gray-300">{s.descricao}</p>
                )}
                {s.valorSugerido != null && (
                  <div className="text-gray-800 dark:text-gray-200">
                    Valor sugerido:{' '}
                    <span className="font-semibold">
                      {Number(s.valorSugerido).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </div>
                )}
              </div>

              {/* Rodapé */}
              <div className="p-3 flex items-center justify-end gap-2 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
                <ActionBtn tone="neutral" icon={Pencil} onClick={() => handleEditar(s)}>
                  Editar
                </ActionBtn>
                <ActionBtn tone="danger" icon={Trash2} onClick={() => handleExcluir(s.id)}>
                  Excluir
                </ActionBtn>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paginação */}
      <div className="flex justify-center items-center gap-4 mt-6">
        <ActionBtn
          tone="neutral"
          icon={ArrowLeft}
          onClick={() => { setPagina(p => Math.max(p - 1, 1)); buscarServicos(false, true); }}
          disabled={pagina <= 1}
        >
          Anterior
        </ActionBtn>

        <span className="text-sm text-gray-700 dark:text-gray-200">Página {pagina}</span>

        <ActionBtn
          tone="neutral"
          icon={ArrowRight}
          onClick={() => { buscarServicos(true); setPagina(p => p + 1); }}
          disabled={!temMaisPaginas}
        >
          Próxima
        </ActionBtn>
      </div>

      <ModalNovoServico
        open={formVisivel}
        onClose={handleFecharModal}
        form={form}
        onChange={(field, value) => {
          setForm(f => ({ ...f, [field]: value }));
          if (erros[field]) setErros(e => ({ ...e, [field]: null }));
        }}
        onSave={handleSalvar}
        errors={erros}
        editando={!!editandoServico}
      />
    </div>
  );
};

export default Servicos;
